import { ref, onUnmounted, type Ref } from 'vue'

interface UsePollingOptions {
  interval: number // Required - time between requests in ms
  cleanupTimeout?: number // Optional - time after which polling stops (default 30min)
  executeImmediately?: boolean // Optional - execute immediately when starting, then use interval (default false)
}

interface UsePollingReturn {
  isPolling: Ref<boolean>
  startPolling: (pollFn: () => Promise<boolean>) => void
  stopPolling: () => void
}

/**
 * Generic polling composable that can poll any async function
 *
 * By default, polling waits for the first interval before executing.
 * Set executeImmediately: true to execute immediately when startPolling is called,
 * then continue at the specified interval.
 *
 * @param options - Configuration with required interval, optional cleanup timeout, and execution behavior
 * @returns Object with polling state and control methods
 */
export function usePolling(options: UsePollingOptions): UsePollingReturn {
  const { interval, cleanupTimeout = 30 * 60 * 1000, executeImmediately = false } = options

  const isPolling = ref(false)
  let pollInterval: NodeJS.Timeout | null = null
  let cleanupTimer: NodeJS.Timeout | null = null

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval)
      pollInterval = null
    }

    if (cleanupTimer) {
      clearTimeout(cleanupTimer)
      cleanupTimer = null
    }

    isPolling.value = false
  }

  const startPolling = (pollFn: () => Promise<boolean>) => {
    if (isPolling.value) {
      stopPolling()
    }

    isPolling.value = true

    // Set up cleanup timeout
    cleanupTimer = setTimeout(() => {
      stopPolling()
    }, cleanupTimeout)

    // Create the poll execution function
    const executePoll = async () => {
      try {
        const shouldContinue = await pollFn()

        if (!shouldContinue) {
          stopPolling()
          return
        }
      } catch (error) {
        console.error('❌ Error in poll function:', error)
        // Continue polling on error, but log it
      }
    }

    if (executeImmediately) {
      // Execute immediately first, then start interval
      executePoll()
      pollInterval = setInterval(executePoll, interval)
    } else {
      // Traditional behavior: wait for interval before first execution
      pollInterval = setInterval(executePoll, interval)
    }
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stopPolling()
  })

  return {
    isPolling,
    startPolling,
    stopPolling,
  }
}
